import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useSortable } from '../../hooks/useSortable';
import { usePaginacao } from '../../hooks/usePaginacao';
import { formatDate } from '../../utils/date';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../api/demandas';
import * as modelosApi from '../../api/modelos';
import * as coresApi from '../../api/cores';
import * as tamanhosApi from '../../api/tamanhos';
import * as empresasApi from '../../api/empresas';

function ModalConcluirProducao({ demanda, modeloNome, tamanhoNome, corNome, onClose, onIrParaEstoque }) {
  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">Concluir Produção</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="mb-2" style={{ fontSize: 14 }}>
              Para finalizar a demanda, lance no estoque as entradas produzidas.
            </p>
            <div className="alert alert-info py-2 mb-0" style={{ fontSize: 13 }}>
              <strong>{modeloNome}</strong> — tamanho <strong>{tamanhoNome}</strong>
              {corNome && <> — cor <strong>{corNome}</strong></>}
              <br />Pendente: <strong>{demanda.quantidade_pendente}</strong> un.
              <br /><span className="text-muted">A quantidade já vem sugerida na tela de Estoque — ajuste se produziu mais ou menos.</span>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={onIrParaEstoque}>Ir para Estoque →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrevisaoCell({ demanda, souOperador, onSalva }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(demanda.data_prevista_entrega ? demanda.data_prevista_entrega.slice(0, 10) : '');
  const [loading, setLoading] = useState(false);

  const salvar = async () => {
    setLoading(true);
    try {
      await api.atualizarPrevisao(demanda.id_demanda, valor ? `${valor}T00:00:00` : null);
      setEditando(false);
      onSalva();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao salvar previsão.');
    } finally { setLoading(false); }
  };

  if (!souOperador) {
    return <span>{formatDate(demanda.data_prevista_entrega)}</span>;
  }

  if (!editando) {
    return (
      <button className="btn btn-link btn-sm p-0" onClick={() => setEditando(true)}>
        {demanda.data_prevista_entrega ? formatDate(demanda.data_prevista_entrega) : <span className="text-muted">Definir</span>}
      </button>
    );
  }

  return (
    <div className="d-flex gap-1 align-items-center">
      <input type="date" className="form-control form-control-sm" style={{ width: 140 }}
        value={valor} onChange={e => setValor(e.target.value)} disabled={loading} autoFocus />
      <button className="btn btn-sm btn-primary py-0 px-2" onClick={salvar} disabled={loading}>✓</button>
      <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setEditando(false)} disabled={loading}>✕</button>
    </div>
  );
}

export default function Demandas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const souOperador = ['operador', 'gestor', 'admin'].includes(user?.perfil);
  const souAdmin = user?.perfil === 'admin';
  const emGlobal = souAdmin && !user?.empresa_ativa_id;

  const [items, setItems] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [cores, setCores] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [idEmpresaFiltro, setIdEmpresaFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');
  const [erroCarregar, setErroCarregar] = useState('');
  const [iniciando, setIniciando] = useState(null);
  const [concluindo, setConcluindo] = useState(null);

  const carregar = useCallback((status) => {
    setLoading(true);
    setErroCarregar('');
    Promise.all([
      api.listar(status ? { status } : {}),
      modelosApi.listar(),
      coresApi.listar(),
      tamanhosApi.listar(),
    ]).then(([d, m, c, tam]) => { setItems(d.data); setModelos(m.data); setCores(c.data); setTamanhos(tam.data); })
      .catch(() => setErroCarregar('Erro ao carregar dados. Recarregue a página.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(filtroStatus); }, [carregar, filtroStatus]);

  useEffect(() => {
    if (souAdmin) empresasApi.listEmpresas().then(r => setEmpresas(r.data)).catch(() => {});
  }, [souAdmin]);

  const modeloNome = id => modelos.find(m => m.id_modelo_uniforme === id)?.nome || id;
  const corNome = id => id ? cores.find(c => c.id_cor === id)?.nome : null;
  const tamanhoNome = id => tamanhos.find(t => t.id_tamanho_uniforme === id)?.nome || id;
  const empresaNome = id => empresas.find(e => e.id_empresa_operadora === id)?.nome || '—';

  const irParaEstoque = (demanda) => {
    const params = new URLSearchParams({
      lancar: '1',
      modelo: demanda.id_modelo_uniforme,
      id_tamanho_uniforme: demanda.id_tamanho_uniforme,
      qtd: String(demanda.quantidade_pendente),
    });
    if (demanda.id_cor) params.set('cor', demanda.id_cor);
    navigate(`/estoque?${params.toString()}`);
  };

  const iniciarProducao = async (demanda) => {
    setIniciando(demanda.id_demanda);
    try {
      await api.iniciarProducao(demanda.id_demanda);
      carregar(filtroStatus);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao iniciar produção.');
    } finally { setIniciando(null); }
  };

  const filtrados = items.filter(i => {
    if (emGlobal && idEmpresaFiltro && i.id_empresa_operadora !== idEmpresaFiltro) return false;
    const nome = modeloNome(i.id_modelo_uniforme).toLowerCase();
    return nome.includes(busca.toLowerCase()) || tamanhoNome(i.id_tamanho_uniforme).toLowerCase().includes(busca.toLowerCase());
  });

  const { sorted, Th } = useSortable(filtrados, 'created_at');
  const { paginados, Paginacao } = usePaginacao(sorted);

  const totais = {
    aberta: items.filter(i => i.status === 'aberta').length,
    producao: items.filter(i => i.status === 'em_producao').length,
    parcial: items.filter(i => i.status === 'parcialmente_atendida').length,
    atendida: items.filter(i => i.status === 'atendida').length,
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Demandas de Fabricação" subtitulo="Ordens pendentes de produção" />

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      <div className="row g-2 mb-3">
        {[
          { status: '', label: 'Todas', count: items.length, cls: 'btn-outline-secondary' },
          { status: 'aberta', label: 'Abertas', count: totais.aberta, cls: 'btn-outline-warning' },
          { status: 'em_producao', label: 'Em Produção', count: totais.producao, cls: 'btn-outline-primary' },
          { status: 'parcialmente_atendida', label: 'Parciais', count: totais.parcial, cls: 'btn-outline-info' },
          { status: 'atendida', label: 'Atendidas', count: totais.atendida, cls: 'btn-outline-success' },
        ].map(f => (
          <div key={f.status} className="col-auto">
            <button
              className={`btn btn-sm ${f.cls}${filtroStatus === f.status ? ' active' : ''}`}
              onClick={() => setFiltroStatus(f.status)}>
              {f.label} <span className="badge bg-white text-dark ms-1">{f.count}</span>
            </button>
          </div>
        ))}
      </div>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <input className="form-control" style={{ maxWidth: 360 }}
          placeholder="Buscar por modelo ou tamanho..."
          value={busca} onChange={e => setBusca(e.target.value)} />
        {emGlobal && (
          <select className="form-select" style={{ maxWidth: 220 }}
            value={idEmpresaFiltro} onChange={e => setIdEmpresaFiltro(e.target.value)}>
            <option value="">Todas as empresas</option>
            {empresas.map(e => <option key={e.id_empresa_operadora} value={e.id_empresa_operadora}>{e.nome}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <Th campo="created_at">Data</Th>
                  <th>Modelo</th>
                  {emGlobal && !idEmpresaFiltro && <th>Empresa</th>}
                  <Th campo="id_tamanho_uniforme">Tam.</Th>
                  <Th campo="quantidade_pendente">Qtd. Pendente</Th>
                  <Th campo="status">Status</Th>
                  <th>Previsão Entrega</th>
                  <th>Pedido</th>
                  {souOperador && <th style={{ width: 140 }}></th>}
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 && (
                  <tr><td colSpan={(souOperador ? 8 : 7) + (emGlobal && !idEmpresaFiltro ? 1 : 0)} className="text-center text-muted py-4">Nenhuma demanda encontrada.</td></tr>
                )}
                {paginados.map(d => (
                  <tr key={d.id_demanda}>
                    <td>{formatDate(d.created_at)}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {modeloNome(d.id_modelo_uniforme)}
                    </td>
                    {emGlobal && !idEmpresaFiltro && (
                      <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {empresaNome(d.id_empresa_operadora)}
                      </td>
                    )}
                    <td><span className="badge bg-secondary">{tamanhoNome(d.id_tamanho_uniforme)}</span></td>
                    <td>
                      <span className={`fw-bold ${d.quantidade_pendente > 0 ? 'text-danger' : 'text-muted'}`}>
                        {d.quantidade_pendente}
                      </span>
                    </td>
                    <td><StatusBadge status={d.status} /></td>
                    <td>
                      <PrevisaoCell demanda={d} souOperador={souOperador} onSalva={() => carregar(filtroStatus)} />
                    </td>
                    <td>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        {d.id_pedido?.slice(0, 8)}...
                      </span>
                    </td>
                    {souOperador && (
                      <td>
                        {d.status === 'aberta' && (
                          <button className="btn btn-sm btn-outline-primary py-0 px-2"
                            disabled={iniciando === d.id_demanda}
                            onClick={() => iniciarProducao(d)}>
                            {iniciando === d.id_demanda ? '…' : 'Iniciar Produção'}
                          </button>
                        )}
                        {d.status === 'em_producao' && (
                          <button className="btn btn-sm btn-outline-success py-0 px-2"
                            onClick={() => setConcluindo(d)}>
                            Finalizar Produção
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Paginacao />

      {concluindo && (
        <ModalConcluirProducao
          demanda={concluindo}
          modeloNome={modeloNome(concluindo.id_modelo_uniforme)}
          tamanhoNome={tamanhoNome(concluindo.id_tamanho_uniforme)}
          corNome={corNome(concluindo.id_cor)}
          onClose={() => setConcluindo(null)}
          onIrParaEstoque={() => irParaEstoque(concluindo)}
        />
      )}
    </div>
  );
}
